import type { ReactElement, ReactNode } from "react";
import { useLayoutEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, NavLink, useLocation } from "react-router-dom";

import { Badge } from "@/components/ui/badge";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { Skeleton } from "@/components/ui/skeleton";
import { useSidebarNavScroll } from "@/components/layout/useSidebarNavScroll";
import { useSidebarNavState } from "@/navigation/sidebarRegistry";
import { useAuth } from "@/hooks/useAuth";
import { resolveAppExitRoute, resolveDefaultRoute } from "@/lib/permissions";
import { resolveSettingsReturnTo } from "@/lib/settingsReturnTo";
import type { SidebarNavGroupItem, SidebarNavItem, SidebarNavSection } from "@/navigation/types";
import { firstGroupLink, groupContainsPath } from "@/navigation/navItems";
import {
  APP_CHROME_SIDEBAR_FOOTER_CLASS,
  SIDEBAR_NAV_TOP_PAD_CLASS,
  SIDEBAR_WIDTH_COLLAPSED_CLASS,
  SIDEBAR_WIDTH_EXPANDED_CLASS,
} from "@/components/layout/appChrome";
import { cn } from "@/lib/utils";
import { ArrowLeft, ChevronRight, LayoutDashboard, PanelLeft, Search } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Heading } from "@/components/ui/heading";
import { Input } from "@/components/ui/input";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { TOUR_ANCHOR } from "@/components/product-tour/tourSteps";
import { useHelp } from "@/components/layout/HelpProvider";
import { useUiStore } from "@/store/uiStore";

function normalizeSearch(value: string): string {
  return value
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .trim()
    .toLowerCase();
}

/** True when a truncated label’s text overflows its box (ellipsis is visible). */
function useIsLabelTruncated(text: string): {
  ref: (node: HTMLSpanElement | null) => void;
  truncated: boolean;
} {
  const [node, setNode] = useState<HTMLSpanElement | null>(null);
  const [truncated, setTruncated] = useState(false);

  useLayoutEffect(() => {
    if (!node) {
      setTruncated(false);
      return;
    }

    const measure = (): void => {
      setTruncated(node.scrollWidth > node.clientWidth + 1);
    };

    measure();
    const raf = requestAnimationFrame(measure);
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    const parent = node.parentElement;
    if (parent) observer.observe(parent);
    return () => {
      cancelAnimationFrame(raf);
      observer.disconnect();
    };
  }, [node, text]);

  return { ref: setNode, truncated };
}

export function LogoMark({ className }: { className?: string }): ReactElement {
  return (
    <svg viewBox="0 0 32 32" className={cn("size-8 shrink-0", className)} aria-hidden>
      <rect width="32" height="32" rx="6" className="fill-primary" />
      {/* Stem + arms: join the four corner dots into a K */}
      <path
        d="M10 10 V22 M10 16 L22 10 M10 16 L22 22"
        className="stroke-primary-foreground"
        strokeWidth="0.75"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
      <circle cx="10" cy="10" r="2" className="fill-primary-foreground" />
      <circle cx="22" cy="10" r="2" className="fill-primary-foreground" />
      <circle cx="10" cy="22" r="2" className="fill-primary-foreground" />
      <circle cx="22" cy="22" r="2" className="fill-primary-foreground" />
    </svg>
  );
}

type BrandLogoLinkProps = {
  className?: string;
  logoClassName?: string;
  headingVariant?: "chrome" | "section";
  showLabel?: boolean;
};

export function BrandLogoLink({
  className,
  logoClassName,
  headingVariant = "section",
  showLabel = true,
}: BrandLogoLinkProps): ReactElement {
  const { t } = useTranslation();
  const { user } = useAuth();
  const to = resolveDefaultRoute(user);

  return (
    <Link
      to={to}
      className={cn(
        "flex min-w-0 items-center gap-2.5 outline-none ring-offset-background focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
        className,
      )}
      aria-label={showLabel ? undefined : t("common.mainNav")}
    >
      <LogoMark className={logoClassName} />
      {showLabel ? (
        <Heading variant={headingVariant} as="span" className="truncate">
          KonstruLog
        </Heading>
      ) : null}
    </Link>
  );
}

/** Collapsed rail: full-width hit target + fixed icon box so all glyphs align (Soon rows match NavLink). */
export function SidebarRailIcon({
  icon,
  collapsed,
}: {
  icon: ReactElement;
  collapsed?: boolean;
}): ReactElement {
  if (!collapsed) return icon;
  return (
    <span className="grid size-4 shrink-0 place-items-center [&>svg]:block [&>svg]:size-4">
      {icon}
    </span>
  );
}

export function NavSection({
  title,
  collapsed,
  children,
}: {
  title: string;
  collapsed?: boolean;
  children: ReactNode;
}): ReactElement {
  return (
    <div className="flex flex-col gap-1">
      {collapsed || !title ? null : (
        <div className="px-3 pb-1 text-xs font-medium text-muted-foreground">{title}</div>
      )}
      {children}
    </div>
  );
}

type NavItemProps = {
  to: string;
  end?: boolean;
  icon: ReactElement;
  label: string;
  trailing?: ReactNode;
  onNavigate?: () => void;
  collapsed?: boolean;
};

/** Centers a fixed square hit target in the rail so hover/active backgrounds and icons line up (Soon + links match). */
export function RailIconTooltipSlot({
  label,
  children,
}: {
  label: string;
  children: ReactElement;
}): ReactElement {
  return (
    <div className="flex w-full justify-center">
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="box-border size-10 shrink-0 overflow-hidden rounded-lg">{children}</div>
        </TooltipTrigger>
        <TooltipContent side="right">{label}</TooltipContent>
      </Tooltip>
    </div>
  );
}

export function NavItem({
  to,
  end,
  icon,
  label,
  trailing,
  onNavigate,
  collapsed,
}: NavItemProps): ReactElement {
  if (!collapsed) {
    return (
      <NavLink
        to={to}
        end={end}
        onClick={onNavigate}
        className={({ isActive }) =>
          cn(
            "flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors",
            isActive
              ? "bg-sidebar-accent font-medium text-sidebar-foreground [&_svg]:text-primary"
              : "text-sidebar-foreground/90 hover:bg-sidebar-accent/70 [&_svg]:text-muted-foreground",
          )
        }
      >
        <SidebarRailIcon icon={icon} collapsed={false} />
        <span className="flex-1">{label}</span>
        {trailing}
      </NavLink>
    );
  }

  const railLink = (
    <NavLink
      to={to}
      end={end}
      onClick={onNavigate}
      aria-label={label}
      className={({ isActive }) =>
        cn(
          "flex size-full min-h-0 min-w-0 items-center justify-center rounded-lg text-sm transition-colors outline-none",
          isActive
            ? "bg-sidebar-accent font-medium text-sidebar-foreground [&_svg]:text-primary"
            : "text-sidebar-foreground/90 hover:bg-sidebar-accent/70 [&_svg]:text-muted-foreground",
        )
      }
    >
      <SidebarRailIcon icon={icon} collapsed />
    </NavLink>
  );

  return <RailIconTooltipSlot label={label}>{railLink}</RailIconTooltipSlot>;
}

function SoonItem({
  icon,
  label,
  collapsed,
}: {
  icon: ReactElement;
  label: string;
  collapsed?: boolean;
}): ReactElement {
  const { t } = useTranslation();
  const hint = `${label} (${t("common.soon")})`;

  if (!collapsed) {
    return (
      <div
        className="flex cursor-not-allowed items-center gap-3 rounded-lg px-3 py-2 text-sm opacity-55"
        aria-disabled
      >
        <SidebarRailIcon icon={icon} collapsed={false} />
        <span className="flex min-w-0 items-center gap-1.5">
          <span className="truncate">{label}</span>
          <Badge variant="secondary" className="shrink-0 text-[10px]">
            {t("common.soon")}
          </Badge>
        </span>
      </div>
    );
  }

  const railSoon = (
    <div
      className="flex size-full min-h-0 min-w-0 cursor-not-allowed items-center justify-center rounded-lg text-sm opacity-55 outline-none"
      aria-disabled
      aria-label={hint}
    >
      <SidebarRailIcon icon={icon} collapsed />
    </div>
  );

  return <RailIconTooltipSlot label={hint}>{railSoon}</RailIconTooltipSlot>;
}

function SidebarNavGroup({
  item,
  collapsed,
  onNavigate,
  iconCls,
  forceExpanded = false,
  nested = false,
}: {
  item: SidebarNavGroupItem;
  collapsed?: boolean;
  onNavigate?: () => void;
  iconCls: string;
  forceExpanded?: boolean;
  /** Nested sub-group inside a parent accordion (e.g. Materiais under Cadastros). */
  nested?: boolean;
}): ReactElement {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const Icon = item.icon;
  const first = firstGroupLink(item);
  const groupLabel = t(item.labelKey);
  const { ref: labelRef, truncated: labelTruncated } = useIsLabelTruncated(groupLabel);
  const [flyoutOpen, setFlyoutOpen] = useState(false);
  const [labelTooltipOpen, setLabelTooltipOpen] = useState(false);

  if (!labelTruncated && labelTooltipOpen) {
    setLabelTooltipOpen(false);
  }

  const isGroupActive = groupContainsPath(item, pathname);
  const [expanded, setExpanded] = useState(isGroupActive);
  const [wasGroupActive, setWasGroupActive] = useState(isGroupActive);

  // Auto-expand the group that contains the current page. Do not close others.
  if (isGroupActive !== wasGroupActive) {
    setWasGroupActive(isGroupActive);
    if (isGroupActive) setExpanded(true);
  }

  const effectiveExpanded = forceExpanded || expanded;
  const leafLinks = item.items.filter(
    (sub): sub is Extract<typeof sub, { kind: "link" }> => sub.kind === "link",
  );
  const nestedGroups = item.items.filter((sub): sub is SidebarNavGroupItem => sub.kind === "group");

  if (collapsed) {
    // Nested groups never render in the collapsed rail — parent flyout lists leaves.
    if (nested) return <></>;

    if (!Icon) {
      return (
        <>
          {leafLinks.map((sub) => {
            const SubIcon = sub.icon;
            if (!SubIcon) return null;
            return (
              <NavItem
                key={sub.to}
                to={sub.to}
                end={sub.end}
                icon={<SubIcon className={iconCls} aria-hidden />}
                label={t(sub.labelKey)}
                onNavigate={onNavigate}
                collapsed
              />
            );
          })}
          {nestedGroups.map((sub) => (
            <SidebarNavGroup
              key={sub.labelKey}
              item={sub}
              collapsed
              onNavigate={onNavigate}
              iconCls={iconCls}
              forceExpanded={forceExpanded}
            />
          ))}
        </>
      );
    }

    const allLeaves = [
      ...leafLinks,
      ...nestedGroups.flatMap((g) =>
        g.items.filter((c): c is Extract<typeof c, { kind: "link" }> => c.kind === "link"),
      ),
    ];

    // Collapsed rail: single child stays a direct link; multiple children get a hover flyout.
    if (allLeaves.length <= 1 && first) {
      return (
        <NavItem
          to={first.to}
          end={first.end}
          icon={<Icon className={iconCls} aria-hidden />}
          label={groupLabel}
          onNavigate={onNavigate}
          collapsed
        />
      );
    }

    return (
      <HoverCard open={flyoutOpen} onOpenChange={setFlyoutOpen}>
        <div className="flex w-full justify-center">
          <HoverCardTrigger asChild>
            <button
              type="button"
              aria-label={groupLabel}
              aria-expanded={flyoutOpen}
              aria-haspopup="menu"
              className={cn(
                "box-border flex size-10 shrink-0 items-center justify-center rounded-lg text-sm transition-colors outline-none",
                flyoutOpen || isGroupActive
                  ? "bg-sidebar-accent font-medium text-sidebar-foreground [&_svg]:text-primary"
                  : "text-sidebar-foreground/90 hover:bg-sidebar-accent/70 [&_svg]:text-muted-foreground",
              )}
            >
              <SidebarRailIcon icon={<Icon className={iconCls} aria-hidden />} collapsed />
            </button>
          </HoverCardTrigger>
        </div>
        <HoverCardContent side="right" align="start" className="w-52 rounded-2xl p-2">
          <div className="px-2 pb-1.5 text-xs text-muted-foreground">{groupLabel}</div>
          <div className="flex flex-col gap-0.5" role="menu">
            {item.items.map((sub) => {
              if (sub.kind === "group") {
                return (
                  <div key={sub.labelKey} className="flex flex-col gap-0.5">
                    <div className="px-2 pt-1.5 pb-0.5 text-[11px] font-medium text-muted-foreground">
                      {t(sub.labelKey)}
                    </div>
                    {sub.items.map((leaf) => {
                      if (leaf.kind !== "link") return null;
                      return (
                        <NavLink
                          key={leaf.to}
                          to={leaf.to}
                          end={leaf.end}
                          role="menuitem"
                          onClick={() => {
                            setFlyoutOpen(false);
                            onNavigate?.();
                          }}
                          className={({ isActive }) =>
                            cn(
                              "rounded-lg px-2 py-1.5 text-sm transition-colors",
                              isActive
                                ? "bg-accent font-medium text-accent-foreground"
                                : "text-popover-foreground hover:bg-accent/70",
                            )
                          }
                        >
                          {t(leaf.labelKey)}
                        </NavLink>
                      );
                    })}
                  </div>
                );
              }
              return (
                <NavLink
                  key={sub.to}
                  to={sub.to}
                  end={sub.end}
                  role="menuitem"
                  onClick={() => {
                    setFlyoutOpen(false);
                    onNavigate?.();
                  }}
                  className={({ isActive }) =>
                    cn(
                      "rounded-lg px-2 py-1.5 text-sm transition-colors",
                      isActive
                        ? "bg-accent font-medium text-accent-foreground"
                        : "text-popover-foreground hover:bg-accent/70",
                    )
                  }
                >
                  {t(sub.labelKey)}
                </NavLink>
              );
            })}
          </div>
        </HoverCardContent>
      </HoverCard>
    );
  }

  return (
    <div className="flex flex-col gap-0.5">
      <Tooltip
        open={labelTooltipOpen}
        onOpenChange={(open) => setLabelTooltipOpen(open && labelTruncated)}
      >
        <TooltipTrigger asChild>
          <button
            type="button"
            aria-expanded={effectiveExpanded}
            onClick={() => setExpanded((open) => !open)}
            className={cn(
              "flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors hover:bg-sidebar-accent/70",
              nested && "pl-11",
              isGroupActive
                ? "font-medium text-sidebar-foreground [&_svg]:text-primary"
                : "text-sidebar-foreground/90 [&_svg]:text-muted-foreground",
            )}
          >
            {Icon && !nested ? (
              <SidebarRailIcon icon={<Icon className={iconCls} aria-hidden />} collapsed={false} />
            ) : null}
            <span ref={labelRef} className="min-w-0 flex-1 truncate text-left">
              {groupLabel}
            </span>
            <ChevronRight
              className={cn(
                "size-4 shrink-0 text-muted-foreground transition-transform duration-200",
                effectiveExpanded && "rotate-90",
              )}
              aria-hidden
            />
          </button>
        </TooltipTrigger>
        {labelTruncated ? <TooltipContent side="right">{groupLabel}</TooltipContent> : null}
      </Tooltip>
      {effectiveExpanded
        ? item.items.map((sub) => {
            if (sub.kind === "group") {
              return (
                <SidebarNavGroup
                  key={sub.labelKey}
                  item={sub}
                  onNavigate={onNavigate}
                  iconCls={iconCls}
                  forceExpanded={forceExpanded}
                  nested
                />
              );
            }
            const SubIcon = sub.icon;
            if (!Icon && SubIcon) {
              return (
                <div key={sub.to} className="pl-4">
                  <NavItem
                    to={sub.to}
                    end={sub.end}
                    icon={<SubIcon className={iconCls} aria-hidden />}
                    label={t(sub.labelKey)}
                    onNavigate={onNavigate}
                  />
                </div>
              );
            }
            return (
              <NavLink
                key={sub.to}
                to={sub.to}
                end={sub.end}
                onClick={onNavigate}
                className={({ isActive }) =>
                  cn(
                    "rounded-lg py-1.5 pr-3 text-sm transition-colors",
                    nested ? "pl-14" : "pl-11",
                    isActive
                      ? "bg-sidebar-accent font-medium text-sidebar-foreground"
                      : "text-sidebar-foreground/80 hover:bg-sidebar-accent/70",
                  )
                }
              >
                {t(sub.labelKey)}
              </NavLink>
            );
          })
        : null}
    </div>
  );
}

function SidebarNavItemRenderer({
  item,
  collapsed,
  onNavigate,
  iconCls,
  forceExpanded = false,
}: {
  item: SidebarNavItem;
  collapsed?: boolean;
  onNavigate?: () => void;
  iconCls: string;
  forceExpanded?: boolean;
}): ReactElement {
  const { t } = useTranslation();

  switch (item.kind) {
    case "soon": {
      const Icon = item.icon;
      return (
        <SoonItem
          icon={<Icon className={iconCls} aria-hidden />}
          label={t(item.labelKey)}
          collapsed={collapsed}
        />
      );
    }
    case "group":
      return (
        <SidebarNavGroup
          item={item}
          collapsed={collapsed}
          onNavigate={onNavigate}
          iconCls={iconCls}
          forceExpanded={forceExpanded}
        />
      );
    case "link": {
      const Icon = item.icon;
      return (
        <NavItem
          to={item.to}
          end={item.end}
          icon={<Icon className={iconCls} aria-hidden />}
          label={t(item.labelKey)}
          onNavigate={onNavigate}
          collapsed={collapsed}
        />
      );
    }
    default: {
      const _exhaustive: never = item;
      return _exhaustive;
    }
  }
}

type SidebarPanelsProps = {
  navigateDismiss?: () => void;
  /** Desktop rail only: icon-only mode */
  collapsed?: boolean;
  showCollapseToggle?: boolean;
  onToggleCollapsed?: () => void;
};

type SidebarChromeProps = {
  collapsed?: boolean;
  showCollapseToggle?: boolean;
  onToggleCollapsed?: () => void;
  children: ReactNode;
};

/** Shared sidebar header, nav, and optional footer collapse toggle. */
export function SidebarChrome({
  collapsed = false,
  showCollapseToggle = false,
  onToggleCollapsed,
  children,
}: SidebarChromeProps): ReactElement {
  const { t } = useTranslation();
  const navRef = useSidebarNavScroll();

  const collapseToggle =
    showCollapseToggle && onToggleCollapsed ? (
      <div
        className={cn(
          APP_CHROME_SIDEBAR_FOOTER_CLASS,
          collapsed ? "justify-center px-2" : "justify-end px-3",
        )}
      >
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              className="size-8 shrink-0 text-sidebar-foreground hover:bg-sidebar-accent/70 hover:text-sidebar-foreground"
              aria-expanded={!collapsed}
              aria-label={collapsed ? t("common.expandSidebar") : t("common.collapseSidebar")}
              onClick={onToggleCollapsed}
            >
              <PanelLeft className="size-4" aria-hidden />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="right">
            {collapsed ? t("common.expandSidebar") : t("common.collapseSidebar")}
          </TooltipContent>
        </Tooltip>
      </div>
    ) : null;

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <nav
        ref={navRef}
        className={cn(
          "flex min-h-0 flex-1 flex-col overflow-y-auto overflow-x-hidden",
          SIDEBAR_NAV_TOP_PAD_CLASS,
          collapsed ? "gap-1 px-2 pb-2" : "gap-6 px-3 pb-3",
        )}
      >
        {children}
      </nav>

      {collapseToggle}
    </div>
  );
}

function SidebarNavSkeleton({
  collapsed,
  itemCount = 8,
}: {
  collapsed?: boolean;
  itemCount?: number;
}): ReactElement {
  const widths = [
    "w-[58%]",
    "w-[72%]",
    "w-[64%]",
    "w-[80%]",
    "w-[55%]",
    "w-[68%]",
    "w-[74%]",
    "w-[60%]",
  ];

  return (
    <div
      className="flex flex-col gap-1 motion-reduce:animate-none"
      role="status"
      aria-busy="true"
      aria-label="Loading navigation"
    >
      {collapsed ? null : <Skeleton className="mx-3 mb-2 h-3 w-24 rounded-sm" />}
      {Array.from({ length: itemCount }, (_, index) => (
        <div
          key={index}
          className={cn(
            "flex animate-in items-center rounded-lg px-3 py-2 duration-300 fade-in-0 fill-mode-backwards motion-reduce:animate-none",
            collapsed ? "justify-center py-0" : "gap-3",
          )}
          style={{ animationDelay: `${index * 45}ms` }}
        >
          <Skeleton className={cn("size-4 shrink-0 rounded-md", collapsed && "size-4")} />
          {collapsed ? null : (
            <Skeleton className={cn("h-4 rounded-sm", widths[index % widths.length])} />
          )}
        </div>
      ))}
    </div>
  );
}

/** Filter settings sidebar sections by a search query (matches translated labels). */
function filterSettingsSections(
  sections: SidebarNavSection[],
  query: string,
  translate: (key: string) => string,
): SidebarNavSection[] {
  const q = normalizeSearch(query);
  if (!q) return sections;

  return sections
    .map((section) => {
      const items = section.items
        .map((item): SidebarNavItem | null => {
          if (item.kind === "group") {
            if (normalizeSearch(translate(item.labelKey)).includes(q)) return item;
            const subItems = item.items
              .map((sub) => {
                if (sub.kind === "group") {
                  if (normalizeSearch(translate(sub.labelKey)).includes(q)) return sub;
                  const nested = sub.items.filter(
                    (leaf) =>
                      leaf.kind === "link" && normalizeSearch(translate(leaf.labelKey)).includes(q),
                  );
                  return nested.length > 0 ? { ...sub, items: nested } : null;
                }
                return normalizeSearch(translate(sub.labelKey)).includes(q) ? sub : null;
              })
              .filter((sub): sub is NonNullable<typeof sub> => sub != null);
            return subItems.length > 0 ? { ...item, items: subItems } : null;
          }
          return normalizeSearch(translate(item.labelKey)).includes(q) ? item : null;
        })
        .filter((item): item is SidebarNavItem => item != null);
      return { titleKey: section.titleKey, items };
    })
    .filter((section) => section.items.length > 0);
}

function SettingsSidebarSearch({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
}): ReactElement {
  return (
    <div className="relative">
      <Search
        className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground"
        aria-hidden
      />
      <Input
        type="search"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        aria-label={placeholder}
        className="h-9 rounded-full pl-9"
      />
    </div>
  );
}

/** Shared sidebar chrome for desktop rail and mobile sheet. */
export function SidebarPanels({
  navigateDismiss,
  collapsed = false,
  showCollapseToggle = false,
  onToggleCollapsed,
}: SidebarPanelsProps): ReactElement {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { sidebarId, sections, isLoading } = useSidebarNavState();
  const [settingsQuery, setSettingsQuery] = useState("");

  const iconCls = "size-4 shrink-0";
  const dismiss = navigateDismiss;
  const isSettings = sidebarId === "settings";
  const [wasSettings, setWasSettings] = useState(isSettings);

  // Clear the query when leaving the settings scope (render-time state sync, no effect).
  if (isSettings !== wasSettings) {
    setWasSettings(isSettings);
    if (!isSettings) setSettingsQuery("");
  }

  const hasQuery = isSettings && normalizeSearch(settingsQuery).length > 0;

  const visibleSections = useMemo(
    () => (hasQuery ? filterSettingsSections(sections, settingsQuery, t) : sections),
    [hasQuery, sections, settingsQuery, t],
  );

  const appExitFallback = resolveAppExitRoute(user);
  const appExitTo =
    sidebarId === "account"
      ? appExitFallback
      : sidebarId === "settings"
        ? resolveSettingsReturnTo(appExitFallback)
        : null;

  const scopeBack =
    sidebarId === "account" && appExitTo && appExitTo !== "/profile" && appExitTo !== "/home"
      ? { to: appExitTo, label: t("common.backToApp") }
      : sidebarId === "settings" && appExitTo
        ? { to: appExitTo, label: t("common.backToApp") }
        : sidebarId === "project"
          ? { to: "/projects", label: t("projects.backToList") }
          : null;

  const scopeBackLink = scopeBack ? (
    <div className="hidden lg:block">
      <NavItem
        to={scopeBack.to}
        end
        icon={<ArrowLeft className={iconCls} aria-hidden />}
        label={scopeBack.label}
        onNavigate={dismiss}
        collapsed={collapsed}
      />
    </div>
  ) : null;

  const settingsSearch =
    isSettings && !collapsed ? (
      <SettingsSidebarSearch
        value={settingsQuery}
        onChange={setSettingsQuery}
        placeholder={t("settings.searchPlaceholder")}
      />
    ) : null;

  const mainSettingsLink = isSettings ? (
    <NavItem
      to="/settings"
      end
      icon={<LayoutDashboard className={iconCls} aria-hidden />}
      label={t("settings.nav.main")}
      onNavigate={dismiss}
      collapsed={collapsed}
    />
  ) : null;

  const navChrome =
    scopeBackLink || settingsSearch ? (
      <div className={cn("flex flex-col", collapsed ? "gap-1" : "gap-2")}>
        {scopeBackLink}
        {settingsSearch}
      </div>
    ) : null;

  const navContent = (
    <div
      key={isLoading ? "loading" : sections.map((s) => s.titleKey).join("-")}
      data-tour={sidebarId === "app" ? TOUR_ANCHOR.appNav : undefined}
      className={cn(
        "flex flex-col motion-reduce:animate-none",
        collapsed ? "gap-1" : navChrome ? "gap-2" : "gap-6",
        !collapsed && !isLoading && "animate-in fade-in-0 duration-300",
      )}
    >
      {navChrome}
      {isLoading ? (
        <SidebarNavSkeleton collapsed={collapsed} />
      ) : visibleSections.length === 0 ? (
        <div className="flex flex-col gap-1">
          {mainSettingsLink}
          {hasQuery ? (
            <p className="px-3 py-2 text-sm text-muted-foreground">{t("common.noResults")}</p>
          ) : null}
        </div>
      ) : (
        visibleSections.map((section, index) => (
          <NavSection
            key={section.titleKey}
            title={
              sidebarId === "app" || sidebarId === "project" || sidebarId === "settings"
                ? ""
                : t(section.titleKey)
            }
            collapsed={collapsed}
          >
            {index === 0 ? mainSettingsLink : null}
            {section.items.map((item) => (
              <SidebarNavItemRenderer
                key={item.kind === "link" ? item.to : item.labelKey}
                item={item}
                collapsed={collapsed}
                onNavigate={dismiss}
                iconCls={iconCls}
                forceExpanded={hasQuery}
              />
            ))}
          </NavSection>
        ))
      )}
    </div>
  );

  return (
    <SidebarChrome
      collapsed={collapsed}
      showCollapseToggle={showCollapseToggle}
      onToggleCollapsed={onToggleCollapsed}
    >
      {navContent}
    </SidebarChrome>
  );
}

export function Sidebar(): ReactElement {
  const persistedCollapsed = useUiStore((s) => s.sidebarCollapsed);
  const setSidebarCollapsed = useUiStore((s) => s.setSidebarCollapsed);
  const { chatOpen } = useHelp();
  const collapsed = persistedCollapsed || chatOpen;

  return (
    <aside
      className={cn(
        "sticky top-0 hidden min-h-0 shrink-0 flex-col bg-sidebar text-sidebar-foreground transition-[width] duration-200 ease-out lg:flex lg:h-full lg:min-h-0 lg:flex-col",
        collapsed ? SIDEBAR_WIDTH_COLLAPSED_CLASS : SIDEBAR_WIDTH_EXPANDED_CLASS,
      )}
    >
      <SidebarPanels
        collapsed={collapsed}
        showCollapseToggle={!chatOpen}
        onToggleCollapsed={() => setSidebarCollapsed(!persistedCollapsed)}
      />
    </aside>
  );
}
